import React, {useState, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Modal,
  Pressable,
  TextInput,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {displayService} from '../services';
import {Display} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {getAllTemplateConfigs} from '../templates';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  BodyText,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';
import {KeyboardAware} from '../components/KeyboardAware';

// Build a map of template id -> name for quick lookup
const templateConfigs = getAllTemplateConfigs();
const templateNameMap = Object.fromEntries(
  templateConfigs.map(tc => [tc.id, tc.name])
);

export default function DisplaysListScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen, navigate} = useSettingsNavigation('Displays');
  const dispatch = useAppDispatch();

  const [displays, setDisplays] = useState<Display[]>([]);
  const [maxDisplays, setMaxDisplays] = useState<number>(5);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [displayToDelete, setDisplayToDelete] = useState<Display | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadDisplays = useCallback(async () => {
    const response = await displayService.getDisplays();
    setDisplays(response.displays);
    setMaxDisplays(response.maxDisplays);
  }, []);

  const {isLoading, loadError, isOnline} = useDataLoader(loadDisplays);

  const handleCreateDisplay = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const display = await displayService.createDisplay({
        name: newDisplayName.trim() || undefined,
      });
      setDisplays(prev => [...prev, display]);
      setShowCreateModal(false);
      setNewDisplayName('');
      // Navigate to edit the new display
      navigate('DisplayEdit', {displayId: display.id});
    } catch (error) {
      console.error('Failed to create display', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteDisplay = async () => {
    if (!displayToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await displayService.deleteDisplay(displayToDelete.id);
      setDisplays(prev => prev.filter(d => d.id !== displayToDelete.id));
      setShowDeleteModal(false);
      setDisplayToDelete(null);
    } catch (error) {
      console.error('Failed to delete display', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetDefault = async (display: Display) => {
    if (display.isDefault) return;
    try {
      await displayService.setDefaultDisplay(display.id);
      setDisplays(prev =>
        prev.map(d => ({
          ...d,
          isDefault: d.id === display.id,
        })),
      );
    } catch (error) {
      console.error('Failed to set default display', error);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </SettingsLayout>
    );
  }

  // Offline and couldn't load
  if (loadError === 'network' && displays.length === 0) {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <OfflineBanner />
          <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
        </View>
      </SettingsLayout>
    );
  }

  const canCreateMore = displays.length < maxDisplays;

  return (
    <SettingsLayout
      currentScreen="displays"
      onNavigate={navigateToSettingsScreen}
      onLogout={handleLogout}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <OfflineBanner />
        <View style={styles.header}>
          <View style={styles.headerIconBg}>
            <Text style={styles.headerIcon}>tv</Text>
          </View>
          <Heading>{t('displays.title')}</Heading>
          <BodyText style={styles.subtitle}>{t('displays.description')}</BodyText>
          <Text style={styles.displayCount}>
            {t('displays.displayCount', {
              count: displays.length,
              max: maxDisplays,
            })}
          </Text>
        </View>

        {/* Display List */}
        <View style={styles.displayList}>
          {displays.map(display => (
            <GlassPanel key={display.id} style={styles.displayCard}>
              <View style={styles.displayHeader}>
                <View style={styles.displayInfo}>
                  <Text style={styles.displayName}>{display.name}</Text>
                  {display.isDefault && (
                    <View style={styles.defaultBadge}>
                      <Text style={styles.defaultBadgeText}>
                        {t('displays.default')}
                      </Text>
                    </View>
                  )}
                </View>
                <View style={styles.displayActions}>
                  {!display.isDefault && (
                    <TouchableOpacity
                      style={[styles.actionButton, !isOnline && {opacity: 0.5}]}
                      onPress={() => handleSetDefault(display)}
                      disabled={!isOnline}
                      activeOpacity={0.7}>
                      <Text style={styles.actionIcon}>star_outline</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={() =>
                      navigate('DisplayEdit', {displayId: display.id})
                    }
                    activeOpacity={0.7}>
                    <Text style={styles.actionIcon}>edit</Text>
                  </TouchableOpacity>
                  {!display.isDefault && (
                    <TouchableOpacity
                      style={[styles.actionButton, !isOnline && {opacity: 0.5}]}
                      onPress={() => {
                        setDisplayToDelete(display);
                        setShowDeleteModal(true);
                      }}
                      disabled={!isOnline}
                      activeOpacity={0.7}>
                      <Text style={[styles.actionIcon, styles.deleteIcon]}>
                        delete
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              <View style={styles.displayStats}>
                <View style={styles.statItem}>
                  <Text style={styles.statIcon}>photo</Text>
                  <Text style={styles.statText}>
                    {display.photoCount === 0 ? t('displays.allPhotos') : display.photoCount} {t('displays.photos').toLowerCase()}
                  </Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statIcon}>event</Text>
                  <Text style={styles.statText}>
                    {display.calendarCount === 0 ? t('displays.allCalendars') : display.calendarCount} {t('displays.calendars').toLowerCase()}
                  </Text>
                </View>
                <View style={styles.statItem}>
                  <Text style={styles.statIcon}>dashboard</Text>
                  <Text style={styles.statText}>
                    {t(templateNameMap[display.frameTemplateId] || 'templates.classic.name')}
                  </Text>
                </View>
              </View>

              <View style={styles.displayQuickActions}>
                <TouchableOpacity
                  style={styles.quickActionButton}
                  onPress={() =>
                    navigate('DisplayPhotos', {displayId: display.id})
                  }
                  activeOpacity={0.7}>
                  <Text style={styles.quickActionIcon}>photo_library</Text>
                  <Text style={styles.quickActionText}>{t('displays.photos')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.quickActionButton}
                  onPress={() =>
                    navigate('DisplayCalendars', {displayId: display.id})
                  }
                  activeOpacity={0.7}>
                  <Text style={styles.quickActionIcon}>calendar_month</Text>
                  <Text style={styles.quickActionText}>
                    {t('displays.calendars')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* View Frame Button */}
              <TouchableOpacity
                style={styles.viewFrameButton}
                onPress={() =>
                  navigate('Frame', {displayId: display.id})
                }
                activeOpacity={0.7}>
                <Text style={styles.viewFrameIcon}>visibility</Text>
                <Text style={styles.viewFrameText}>{t('displays.viewFrame')}</Text>
              </TouchableOpacity>
            </GlassPanel>
          ))}

          {/* Create New Display */}
          {canCreateMore ? (
            <TouchableOpacity
              style={[styles.createCard, !isOnline && {opacity: 0.5}]}
              onPress={() => setShowCreateModal(true)}
              disabled={!isOnline}
              activeOpacity={0.7}>
              <Text style={styles.createIcon}>add</Text>
              <Text style={styles.createText}>{t('displays.createDisplay')}</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.limitCard}>
              <Text style={styles.limitIcon}>block</Text>
              <Text style={styles.limitText}>
                {t('displays.limitReached', {max: maxDisplays})}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Create Display Modal */}
      <Modal
        visible={showCreateModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCreateModal(false)}>
        <KeyboardAware style={{flex: 1}}>
        <Pressable
          style={styles.modalOverlay}
          onPress={() => !isCreating && setShowCreateModal(false)}>
          <Pressable style={styles.modalContent} onPress={e => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('displays.createDisplay')}</Text>
            <TextInput
              style={styles.modalInput}
              value={newDisplayName}
              onChangeText={setNewDisplayName}
              placeholder={t('displays.displayNamePlaceholder')}
              placeholderTextColor={theme.textMuted}
              autoFocus
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowCreateModal(false)}
                disabled={isCreating}>
                <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={handleCreateDisplay}
                disabled={isCreating}>
                {isCreating ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.primaryButtonText}>
                    {t('displays.createDisplay')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
        </KeyboardAware>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}>
        <Pressable
          style={styles.modalOverlay}
          onPress={() => !isDeleting && setShowDeleteModal(false)}>
          <Pressable style={styles.modalContent} onPress={e => e.stopPropagation()}>
            <Text style={styles.modalTitle}>{t('displays.confirmDelete')}</Text>
            <Text style={styles.modalText}>{t('displays.confirmDeleteText')}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setShowDeleteModal(false)}
                disabled={isDeleting}>
                <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.deleteButton}
                onPress={handleDeleteDisplay}
                disabled={isDeleting}>
                {isDeleting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.deleteButtonText}>
                    {t('displays.deleteDisplay')}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Header
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  headerIconBg: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 32,
    color: theme.primary,
  },
  subtitle: {
    color: theme.textMuted,
    textAlign: 'center',
    marginTop: 8,
  },
  displayCount: {
    color: theme.textMuted,
    fontSize: 14,
    marginTop: 8,
  },

  // Display List
  displayList: {
    gap: 16,
  },
  displayCard: {
    padding: 20,
  },
  displayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  displayInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  displayName: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.text,
  },
  defaultBadge: {
    backgroundColor: theme.primaryLight,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  defaultBadgeText: {
    color: theme.primary,
    fontSize: 12,
    fontWeight: '600',
  },
  displayActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.textMuted,
  },
  deleteIcon: {
    color: theme.error,
  },

  // Stats
  displayStats: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 16,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 18,
    color: theme.textMuted,
  },
  statText: {
    color: theme.textMuted,
    fontSize: 14,
  },

  // Quick Actions
  displayQuickActions: {
    flexDirection: 'row',
    gap: 12,
  },
  quickActionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 12,
    borderRadius: 12,
  },
  quickActionIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.primary,
  },
  quickActionText: {
    color: theme.text,
    fontSize: 14,
    fontWeight: '500',
  },

  // View Frame Button
  viewFrameButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.primary,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 16,
  },
  viewFrameIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: '#1c1917',
  },
  viewFrameText: {
    color: '#1c1917',
    fontSize: 14,
    fontWeight: '600',
  },

  // Create Card
  createCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
    backgroundColor: theme.surface,
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.borderLight,
  } as any,
  createIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 28,
    color: theme.primary,
  },
  createText: {
    color: theme.text,
    fontSize: 16,
    fontWeight: '600',
  },

  // Limit Card
  limitCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 24,
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderRadius: 16,
  },
  limitIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.error,
  },
  limitText: {
    color: theme.textMuted,
    fontSize: 14,
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.surface,
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    color: theme.text,
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalText: {
    color: theme.textMuted,
    fontSize: 14,
    marginBottom: 24,
    lineHeight: 20,
    textAlign: 'center',
  },
  modalInput: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: 16,
    color: theme.text,
    fontSize: 16,
    marginBottom: 24,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  cancelButtonText: {
    color: theme.text,
    fontSize: 14,
    fontWeight: '500',
  },
  primaryButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    backgroundColor: theme.primary,
    minWidth: 100,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    backgroundColor: theme.error,
    minWidth: 100,
    alignItems: 'center',
  },
  deleteButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});
